/**
 * Utility functions for markdown and URL handling in Q&A Review
 */

/**
 * Strip markdown syntax and extract plain text
 */
export function stripMarkdown(text: string): string {
  if (!text) return '';
  
  // Remove code blocks
  text = text.replace(/```[\s\S]*?```/g, '');
  text = text.replace(/`[^`]*`/g, '');
  
  // Remove links but keep text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  // Remove images
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '');
  
  // Remove headers
  text = text.replace(/^#{1,6}\s+/gm, '');
  
  // Remove bold/italic
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');
  
  // Remove lists
  text = text.replace(/^[\*\-\+]\s+/gm, '');
  text = text.replace(/^\d+\.\s+/gm, '');
  
  // Remove blockquotes
  text = text.replace(/^>\s+/gm, '');
  
  // Remove horizontal rules
  text = text.replace(/^---$/gm, '');
  text = text.replace(/^___$/gm, '');
  text = text.replace(/^\*\*\*$/gm, '');
  
  // Remove citation markers [1], [2], etc.
  text = text.replace(/\[\d+\]/g, '');
  
  // Clean up extra whitespace
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]+/g, ' ');
  
  return text.trim();
}

/**
 * Extract URLs from text (both citation markers and actual URLs)
 */
export function extractUrls(text: string, sourceUrl?: string | null, context?: any): string[] {
  const urls: string[] = [];
  
  // Add source_url if available
  if (sourceUrl) {
    urls.push(sourceUrl);
  }
  
  // Extract from context if available
  if (context) {
    if (typeof context === 'string') {
      try {
        const parsed = JSON.parse(context);
        if (parsed.source_url) {
          urls.push(parsed.source_url);
        }
        if (parsed.urls && Array.isArray(parsed.urls)) {
          urls.push(...parsed.urls);
        }
      } catch (e) {
        // Not JSON, ignore
      }
    } else if (typeof context === 'object') {
      if (context.source_url) {
        urls.push(context.source_url);
      }
      if (context.urls && Array.isArray(context.urls)) {
        urls.push(...context.urls);
      }
    }
  }
  
  // Extract URLs from text (http/https links)
  const urlRegex = /https?:\/\/[^\s\)]+/g;
  const matches = text.match(urlRegex);
  if (matches) {
    urls.push(...matches);
  }
  
  // Remove duplicates
  return Array.from(new Set(urls));
}

/**
 * Truncate URL for display
 */
export function truncateUrl(url: string, maxLength: number = 50): string {
  if (!url) return '';
  if (url.length <= maxLength) return url;
  
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');
    const path = urlObj.pathname;
    
    if (domain.length + path.length <= maxLength) {
      return `${domain}${path}`;
    }
    
    const availableLength = maxLength - domain.length - 3; // 3 for "..."
    if (availableLength > 0) {
      return `${domain}${path.substring(0, availableLength)}...`;
    }
    
    return `${domain}...`;
  } catch (e) {
    // If URL parsing fails, just truncate the string
    return url.length > maxLength ? url.substring(0, maxLength - 3) + '...' : url;
  }
}

/**
 * Replace citation markers [n] with clickable URLs
 */
export function replaceCitationMarkersWithUrls(
  text: string,
  urls: string[]
): string {
  if (!text || urls.length === 0) return text;
  
  // Replace [n] markers with clickable links
  return text.replace(/\[(\d+)\]/g, (match, numStr) => {
    const index = parseInt(numStr, 10) - 1;
    if (index >= 0 && index < urls.length) {
      const url = urls[index];
      const truncated = truncateUrl(url, 40);
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="citation-url-link">${truncated}</a>`;
    }
    return match;
  });
}

